import { Component, computed, debounced, effect, inject, resource, signal } from '@angular/core';
import { DialogComponent } from 'common/dialog/dialog.component';
import { DialogHeaderTitleDirective } from 'common/dialog/dialog-slots.directive';
import { TranslateModule } from '@ngx-translate/core';
import { CardButtonsComponent } from 'common/card/buttons/card-buttons.component';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { form, FormField, FormRoot } from '@angular/forms/signals';
import {
  AggregatedProvidedEvidence,
  ApiV1Service,
  EvidenceFilterType,
  FileAttachmentFilterType,
  ProductRevisionFilterType,
  ViewService,
} from 'api';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom, map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatOption, MatSelect } from '@angular/material/select';
import { EvidenceIntake } from 'component/details/requirement-intake/requirements-detail/evidence-slots/evidence-slot-card/evidence-slot-card.component';
import { EvidenceResultsComponent } from 'component/details/requirement-intake/requirements-detail/evidence-slots/evidence-reuse/evidence-results.component';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { FileTypeLabelComponent } from 'common/files/file-type-label.component';
import { DownloadService } from 'common/files/download.service';
import { ScrollableCardComponent } from 'common/card/scrollable-card/scrollable-card.component';
import { ButtonDirective } from 'common/button/button.directive';

export interface ReuseEvidenceDialogData {
  readonly evidence: EvidenceIntake;
}

type Step = 'select-evidence' | 'select-files';

type EvidenceFilterFormModel = {
  searchText: string;
  fileAttachmentFilter: FileAttachmentFilterType;
  productRevisionFilter: ProductRevisionFilterType;
};

type SelectFilesFormModel = {
  fileAttachmentIds: number[];
};

@Component({
  selector: 'comp-reuse-evidence-dialog',
  templateUrl: './reuse-evidence-dialog.component.html',
  styleUrls: ['./reuse-evidence-dialog.component.scss'],
  imports: [
    DialogComponent,
    DialogHeaderTitleDirective,
    TranslateModule,
    CardButtonsComponent,
    MatDialogClose,
    MatFormField,
    MatInput,
    FormRoot,
    FormField,
    MatLabel,
    MatSelect,
    MatOption,
    EvidenceResultsComponent,
    MatCheckbox,
    FileTypeLabelComponent,
    ScrollableCardComponent,
    ButtonDirective,
  ],
})
export class ReuseEvidenceDialogComponent {
  private readonly viewService = inject(ViewService);
  private readonly apiService = inject(ApiV1Service);
  readonly downloadService = inject(DownloadService);
  private readonly route = inject(ActivatedRoute);
  private readonly dialogRef = inject(MatDialogRef<ReuseEvidenceDialogComponent>);

  private readonly data = signal<ReuseEvidenceDialogData>(
    inject<ReuseEvidenceDialogData>(MAT_DIALOG_DATA),
  );

  readonly step = signal<Step>('select-evidence');

  readonly evidenceFilterType = signal<EvidenceFilterType>('THIS_REVISION');
  readonly evidenceFilterTypes = Object.values(EvidenceFilterType);

  private readonly fileAttachmentFilterType = computed(
    () => this.evidenceFilterFormModel().fileAttachmentFilter,
  );
  readonly fileAttachmentFilterTypes = Object.values(FileAttachmentFilterType);

  private readonly productRevisionFilterType = computed(
    () => this.evidenceFilterFormModel().productRevisionFilter,
  );
  readonly productRevisionFilterTypes = Object.values(ProductRevisionFilterType);

  readonly evidenceFilterFormModel = signal<EvidenceFilterFormModel>({
    searchText: '',
    fileAttachmentFilter: 'ALL',
    productRevisionFilter: 'PREVIOUS',
  });

  readonly evidenceFilterForm = form(this.evidenceFilterFormModel);

  readonly selectFilesFormModel = signal<SelectFilesFormModel>({
    fileAttachmentIds: [],
  });

  readonly selectFilesForm = form(this.selectFilesFormModel);

  readonly selectedFileAttachmentIds = computed(
    () => this.selectFilesFormModel().fileAttachmentIds,
  );

  readonly fileAttachmentIdsToSubmit = computed(() => {
    if (this.step() === 'select-evidence') {
      return (
        this.selectedEvidence()?.fileAttachments.map((fileAttachment) => fileAttachment.id) ?? []
      );
    } else {
      return this.selectedFileAttachmentIds();
    }
  });

  private readonly revisionId = toSignal(
    this.route.paramMap.pipe(map((paramMap) => paramMap.get('revisionId'))),
    {
      initialValue: this.route.snapshot.paramMap.get('revisionId'),
    },
  );

  readonly debouncedSearchText = debounced(() => this.evidenceFilterFormModel().searchText, 300);

  private readonly evidenceQueryParams = computed(() => ({
    revisionId: Number(this.revisionId()),
    evidenceId: this.data().evidence.id,
    searchText: this.debouncedSearchText.value() || undefined,
    evidenceFilter: this.evidenceFilterType(),
    productRevisionFilter: this.productRevisionFilterType(),
    fileAttachmentFilter: this.fileAttachmentFilterType(),
  }));

  private readonly resetSelectionOnQueryChange = effect(() => {
    // Read signal to trigger the effect on change
    this.evidenceQueryParams();
    this.selectedEvidence.set(undefined);
  });

  readonly evidencesResource = resource({
    params: this.evidenceQueryParams,

    loader: ({ params }) => {
      return firstValueFrom(
        this.viewService.getReusableEvidence(
          params.evidenceId,
          params.revisionId,
          params.fileAttachmentFilter,
          params.evidenceFilter,
          params.productRevisionFilter,
          params.searchText,
        ),
      );
    },
  });

  readonly selectedEvidence = signal<AggregatedProvidedEvidence | undefined>(undefined);
  readonly selectedEvidenceFileAttachments = computed(() => {
    return this.selectedEvidence()?.fileAttachments ?? [];
  });

  private readonly resetSelectedFilesOnSelectionChange = effect(() => {
    const fileAttachmentIds =
      this.selectedEvidence()?.fileAttachments.map((fileAttachment) => fileAttachment.id) ?? [];
    this.selectFilesFormModel.set({ fileAttachmentIds });
  });

  setFilterType(evidenceFilterType: EvidenceFilterType) {
    this.evidenceFilterType.set(evidenceFilterType);
    this.evidenceFilterFormModel.set({
      searchText: '',
      fileAttachmentFilter: 'ALL',
      productRevisionFilter: 'PREVIOUS',
    });
  }

  onEvidenceSelected(evidence: AggregatedProvidedEvidence | undefined) {
    this.selectedEvidence.set(evidence);
  }

  isFileAttachmentSelected(fileAttachmentId: number): boolean {
    return this.selectedFileAttachmentIds().includes(fileAttachmentId);
  }

  updateFileAttachmentSelection(fileAttachmentId: number, event: MatCheckboxChange): void {
    const selectedIds = this.selectedFileAttachmentIds();

    this.selectFilesFormModel.set({
      fileAttachmentIds: event.checked
        ? [...selectedIds, fileAttachmentId]
        : selectedIds.filter((selectedId) => selectedId !== fileAttachmentId),
    });
  }

  goBack() {
    this.step.set('select-evidence');
  }

  nextPage() {
    this.step.set('select-files');
  }

  async submit() {
    await firstValueFrom(
      this.apiService.copyFileAttachments(
        this.fileAttachmentIdsToSubmit(),
        this.data().evidence.id,
      ),
    );
    this.dialogRef.close(true);
  }
}
